import { z } from 'zod';

// src/branded.ts
var asTenantId = (id) => id;
var asClassId = (id) => id;
var asStudentId = (id) => id;
var asTeacherId = (id) => id;
var asParentId = (id) => id;
var asSpaceId = (id) => id;
var asStoryPointId = (id) => id;
var asItemId = (id) => id;
var asExamId = (id) => id;
var asSubmissionId = (id) => id;
var asUserId = (id) => id;
var asSessionId = (id) => id;
var asAgentId = (id) => id;
var asAcademicSessionId = (id) => id;

// src/identity/membership.ts
var DEFAULT_TEACHER_PERMISSIONS = {
  canCreateExams: true,
  canEditRubrics: true,
  canManuallyGrade: true,
  canViewAllExams: false,
  canCreateSpaces: false,
  canManageContent: false,
  canViewAnalytics: false,
  canConfigureAgents: false,
  managedSpaceIds: [],
  managedClassIds: []
};
var DEFAULT_STAFF_PERMISSIONS = {
  canManageUsers: false,
  canManageClasses: false,
  canManageBilling: false,
  canViewAnalytics: true,
  canManageSettings: false,
  canExportData: false
};

// src/identity/claims.ts
var MAX_CLAIM_CLASS_IDS = 15;

// src/content/item.ts
var AUTO_EVALUATABLE_TYPES = [
  "mcq",
  "mcaq",
  "true-false",
  "numerical",
  "fill-blanks",
  "fill-blanks-dd",
  "matching",
  "jumbled",
  "group-options"
];
var AI_EVALUATABLE_TYPES = [
  "text",
  "paragraph",
  "code",
  "audio",
  "image_evaluation",
  "chat_agent_question"
];

// src/constants/grades.ts
var GRADE_THRESHOLDS = [
  { min: 90, grade: "A+" },
  { min: 80, grade: "A" },
  { min: 70, grade: "B+" },
  { min: 60, grade: "B" },
  { min: 50, grade: "C" },
  { min: 40, grade: "D" },
  { min: 0, grade: "F" }
];
var BLOOMS_LEVELS = [
  "remember",
  "understand",
  "apply",
  "analyze",
  "evaluate",
  "create"
];
var SUBMISSION_PIPELINE_STATUSES = [
  "uploaded",
  "ocr_processing",
  "ocr_failed",
  "scouting",
  "scouting_failed",
  "scouting_complete",
  "grading",
  "grading_partial",
  "grading_failed",
  "grading_complete",
  "finalization_failed",
  "ready_for_review",
  "reviewed",
  "failed",
  "manual_review_needed"
];
var QUESTION_GRADING_STATUSES = [
  "pending",
  "processing",
  "graded",
  "needs_review",
  "failed",
  "manual",
  "overridden"
];
var EXAM_STATUSES = [
  "draft",
  "question_paper_uploaded",
  "question_paper_extracted",
  "published",
  "grading",
  "completed",
  "results_released",
  "archived"
];

// src/error-types.ts
var APP_ERROR_TO_HTTPS = {
  VALIDATION_FAILED: "invalid-argument",
  NOT_FOUND: "not-found",
  PERMISSION_DENIED: "permission-denied",
  UNAUTHENTICATED: "unauthenticated",
  RATE_LIMITED: "resource-exhausted",
  CONFLICT: "already-exists",
  PRECONDITION_FAILED: "failed-precondition",
  INTERNAL_ERROR: "internal",
  QUOTA_EXCEEDED: "resource-exhausted"
};
var HTTPS_TO_APP_ERROR = {
  "invalid-argument": "VALIDATION_FAILED",
  "not-found": "NOT_FOUND",
  "permission-denied": "PERMISSION_DENIED",
  unauthenticated: "UNAUTHENTICATED",
  "resource-exhausted": "RATE_LIMITED",
  "already-exists": "CONFLICT",
  "failed-precondition": "PRECONDITION_FAILED",
  internal: "INTERNAL_ERROR"
};
var RATE_LIMITS = {
  /** save*, create*, bulk* endpoints */
  WRITE: { maxPerMinute: 30, actionType: "write" },
  /** list*, get*, manage* endpoints */
  READ: { maxPerMinute: 60, actionType: "read" },
  /** sendChatMessage, evaluateAnswer, extractQuestions, gradeQuestion(retry) */
  AI: { maxPerMinute: 10, actionType: "ai" },
  /** switchActiveTenant, joinTenant */
  AUTH: { maxPerMinute: 10, actionType: "auth" },
  /** generateReport */
  REPORT: { maxPerMinute: 5, actionType: "report" }
};
var ERROR_MESSAGES = {
  VALIDATION_FAILED: "The request contains invalid data.",
  NOT_FOUND: "The requested resource was not found.",
  PERMISSION_DENIED: "You do not have permission to perform this action.",
  UNAUTHENTICATED: "You must be logged in to perform this action.",
  RATE_LIMITED: "Too many requests. Please try again in a moment.",
  CONFLICT: "This resource already exists.",
  PRECONDITION_FAILED: "The operation cannot be performed in the current state.",
  INTERNAL_ERROR: "An unexpected error occurred. Please try again.",
  QUOTA_EXCEEDED: "You have exceeded your usage quota."
};
var ERROR_RECOVERY_HINTS = {
  VALIDATION_FAILED: "Check the form fields and try again.",
  NOT_FOUND: "The item may have been deleted. Try refreshing the page.",
  PERMISSION_DENIED: "Contact your administrator if you need access.",
  UNAUTHENTICATED: "Please sign in and try again.",
  RATE_LIMITED: "Wait a few seconds before trying again.",
  CONFLICT: "Refresh the page to see the latest version.",
  PRECONDITION_FAILED: "Refresh the page and verify the current status.",
  INTERNAL_ERROR: "If the problem persists, contact support.",
  QUOTA_EXCEEDED: "Contact your administrator to upgrade your plan."
};

// src/type-guards.ts
function isQuestionItem(item) {
  return item.type === "question";
}
function isMaterialItem(item) {
  return item.type === "material";
}
function isAutoEvaluatable(questionType) {
  return AUTO_EVALUATABLE_TYPES.includes(questionType);
}
function isAIEvaluatable(questionType) {
  return AI_EVALUATABLE_TYPES.includes(questionType);
}
function isFirestoreTimestamp(value) {
  return typeof value === "object" && value !== null && "seconds" in value && "nanoseconds" in value && typeof value.seconds === "number" && typeof value.nanoseconds === "number";
}
function isNonEmptyString(value) {
  return typeof value === "string" && value.trim().length > 0;
}
function isValidDocumentId(value) {
  return typeof value === "string" && value.length > 0 && value.length <= 1500 && !value.includes("/");
}
var MAX_SHORT_TEXT = 200;
var MAX_MEDIUM_TEXT = 2e3;
var MAX_LONG_TEXT = 1e4;
var MAX_ARRAY_ITEMS = 100;
var MAX_BULK_ITEMS = 500;
var firestoreId = z.string().min(1, "ID cannot be empty").max(1500).regex(/^[^/]+$/, "ID cannot contain slashes");
var RubricCriterionLevelSchema = z.object({
  score: z.number(),
  label: z.string(),
  description: z.string()
});
var RubricCriterionSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().optional(),
  maxPoints: z.number(),
  weight: z.number().optional(),
  levels: z.array(RubricCriterionLevelSchema).optional()
});
var EvaluationDimensionSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string(),
  icon: z.string().optional(),
  priority: z.enum(["HIGH", "MEDIUM", "LOW"]),
  promptGuidance: z.string(),
  enabled: z.boolean(),
  isDefault: z.boolean(),
  isCustom: z.boolean(),
  expectedFeedbackCount: z.number().optional(),
  weight: z.number(),
  scoringScale: z.number()
});
var UnifiedRubricSchema = z.object({
  scoringMode: z.enum(["criteria_based", "dimension_based", "holistic", "hybrid"]),
  criteria: z.array(RubricCriterionSchema).optional(),
  dimensions: z.array(EvaluationDimensionSchema).optional(),
  holisticGuidance: z.string().optional(),
  holisticMaxScore: z.number().optional(),
  passingPercentage: z.number().optional(),
  showModelAnswer: z.boolean().optional(),
  modelAnswer: z.string().optional(),
  evaluatorGuidance: z.string().optional()
});
var ItemMetadataSchema = z.object({
  subject: z.string().optional(),
  topics: z.array(z.string()).optional(),
  bloomsLevel: z.string().optional(),
  estimatedTime: z.number().optional(),
  source: z.string().optional()
}).passthrough();
var SaveTenantRequestSchema = z.object({
  id: firestoreId.optional(),
  data: z.object({
    name: z.string().max(MAX_SHORT_TEXT).optional(),
    shortName: z.string().max(MAX_SHORT_TEXT).optional(),
    description: z.string().max(MAX_MEDIUM_TEXT).optional(),
    contactEmail: z.string().email("Invalid email format").max(MAX_SHORT_TEXT).optional(),
    contactPhone: z.string().max(20).nullable().optional(),
    contactPerson: z.string().max(MAX_SHORT_TEXT).nullable().optional(),
    logoUrl: z.string().nullable().optional(),
    bannerUrl: z.string().nullable().optional(),
    website: z.string().nullable().optional(),
    address: z.object({
      street: z.string().optional(),
      city: z.string().optional(),
      state: z.string().optional(),
      country: z.string().optional(),
      zipCode: z.string().optional()
    }).optional(),
    status: z.enum(["active", "suspended", "trial", "expired", "deactivated"]).optional(),
    subscription: z.object({
      plan: z.enum(["free", "trial", "basic", "premium", "enterprise"]).optional(),
      maxStudents: z.number().optional(),
      maxTeachers: z.number().optional(),
      maxSpaces: z.number().optional(),
      maxExamsPerMonth: z.number().optional(),
      billingCycle: z.enum(["monthly", "annual"]).optional(),
      billingEmail: z.string().optional(),
      cancelAtPeriodEnd: z.boolean().optional()
    }).passthrough().optional(),
    features: z.object({
      autoGradeEnabled: z.boolean().optional(),
      levelUpEnabled: z.boolean().optional(),
      scannerAppEnabled: z.boolean().optional(),
      aiChatEnabled: z.boolean().optional(),
      aiGradingEnabled: z.boolean().optional(),
      analyticsEnabled: z.boolean().optional(),
      parentPortalEnabled: z.boolean().optional(),
      bulkImportEnabled: z.boolean().optional(),
      apiAccessEnabled: z.boolean().optional()
    }).optional(),
    settings: z.object({
      geminiKeyRef: z.string().optional(),
      geminiKeySet: z.boolean().optional(),
      defaultEvaluationSettingsId: z.string().optional(),
      defaultAiModel: z.string().optional(),
      timezone: z.string().optional(),
      locale: z.string().optional(),
      gradingPolicy: z.string().optional()
    }).optional(),
    branding: z.object({
      logoUrl: z.string().optional(),
      bannerUrl: z.string().optional(),
      primaryColor: z.string().optional(),
      accentColor: z.string().optional(),
      favicon: z.string().optional()
    }).optional(),
    onboarding: z.object({
      completed: z.boolean().optional(),
      completedSteps: z.array(z.string()).optional()
    }).optional(),
    geminiApiKey: z.string().optional()
  })
});
var DeactivateTenantRequestSchema = z.object({
  tenantId: firestoreId,
  reason: z.string().max(MAX_MEDIUM_TEXT).optional()
});
var ReactivateTenantRequestSchema = z.object({
  tenantId: firestoreId
});
var ExportTenantDataRequestSchema = z.object({
  tenantId: firestoreId,
  format: z.enum(["json", "csv"]),
  collections: z.array(z.enum(["students", "teachers", "classes", "exams", "submissions"]))
});
var SaveStaffRequestSchema = z.object({
  id: firestoreId.optional(),
  tenantId: firestoreId,
  data: z.object({
    uid: z.string().optional(),
    department: z.string().optional(),
    staffPermissions: z.object({
      canManageUsers: z.boolean().optional(),
      canManageClasses: z.boolean().optional(),
      canManageBilling: z.boolean().optional(),
      canViewAnalytics: z.boolean().optional(),
      canManageSettings: z.boolean().optional(),
      canExportData: z.boolean().optional()
    }).optional(),
    status: z.enum(["active", "archived"]).optional(),
    firstName: z.string().optional(),
    lastName: z.string().optional(),
    email: z.string().optional(),
    phone: z.string().optional(),
    password: z.string().optional()
  })
});
var SaveClassRequestSchema = z.object({
  id: firestoreId.optional(),
  tenantId: firestoreId,
  data: z.object({
    name: z.string().max(MAX_SHORT_TEXT).optional(),
    grade: z.string().max(20).optional(),
    section: z.string().max(20).optional(),
    academicSessionId: firestoreId.optional(),
    teacherIds: z.array(firestoreId).max(MAX_ARRAY_ITEMS).optional(),
    status: z.enum(["active", "archived", "deleted"]).optional()
  })
});
var SaveStudentRequestSchema = z.object({
  id: firestoreId.optional(),
  tenantId: firestoreId,
  data: z.object({
    uid: z.string().optional(),
    rollNumber: z.string().max(50).optional(),
    section: z.string().max(20).optional(),
    classIds: z.array(firestoreId).max(MAX_ARRAY_ITEMS).optional(),
    parentIds: z.array(firestoreId).max(MAX_ARRAY_ITEMS).optional(),
    grade: z.string().max(20).optional(),
    admissionNumber: z.string().max(50).optional(),
    dateOfBirth: z.string().max(20).optional(),
    status: z.enum(["active", "archived"]).optional(),
    firstName: z.string().max(MAX_SHORT_TEXT).optional(),
    lastName: z.string().max(MAX_SHORT_TEXT).optional(),
    email: z.string().email("Invalid email format").max(MAX_SHORT_TEXT).optional(),
    phone: z.string().max(20).optional(),
    password: z.string().min(6).max(128).optional()
  })
});
var SaveTeacherRequestSchema = z.object({
  id: firestoreId.optional(),
  tenantId: firestoreId,
  data: z.object({
    uid: z.string().optional(),
    subjects: z.array(z.string().max(MAX_SHORT_TEXT)).max(MAX_ARRAY_ITEMS).optional(),
    designation: z.string().max(MAX_SHORT_TEXT).optional(),
    classIds: z.array(firestoreId).max(MAX_ARRAY_ITEMS).optional(),
    permissions: z.object({
      canCreateExams: z.boolean().optional(),
      canEditRubrics: z.boolean().optional(),
      canManuallyGrade: z.boolean().optional(),
      canViewAllExams: z.boolean().optional(),
      canCreateSpaces: z.boolean().optional(),
      canManageContent: z.boolean().optional(),
      canViewAnalytics: z.boolean().optional(),
      canConfigureAgents: z.boolean().optional(),
      managedSpaceIds: z.array(z.string()).optional(),
      managedClassIds: z.array(z.string()).optional()
    }).optional(),
    status: z.enum(["active", "archived"]).optional(),
    firstName: z.string().max(MAX_SHORT_TEXT).optional(),
    lastName: z.string().max(MAX_SHORT_TEXT).optional(),
    email: z.string().email("Invalid email format").max(MAX_SHORT_TEXT).optional(),
    phone: z.string().max(20).optional(),
    password: z.string().min(6).max(128).optional()
  })
});
var SaveParentRequestSchema = z.object({
  id: firestoreId.optional(),
  tenantId: firestoreId,
  data: z.object({
    uid: z.string().optional(),
    childStudentIds: z.array(firestoreId).max(MAX_ARRAY_ITEMS).optional(),
    status: z.enum(["active", "archived"]).optional(),
    firstName: z.string().max(MAX_SHORT_TEXT).optional(),
    lastName: z.string().max(MAX_SHORT_TEXT).optional(),
    email: z.string().email("Invalid email format").max(MAX_SHORT_TEXT).optional(),
    phone: z.string().max(20).optional(),
    password: z.string().min(6).max(128).optional()
  })
});
var SaveAcademicSessionRequestSchema = z.object({
  id: firestoreId.optional(),
  tenantId: firestoreId,
  data: z.object({
    name: z.string().optional(),
    startDate: z.string().optional(),
    endDate: z.string().optional(),
    isCurrent: z.boolean().optional(),
    status: z.enum(["active", "archived"]).optional()
  })
});
var ManageNotificationsRequestSchema = z.object({
  tenantId: firestoreId,
  action: z.enum(["list", "markRead"]),
  limit: z.number().optional(),
  cursor: z.string().optional(),
  notificationId: z.string().optional(),
  markAllRead: z.boolean().optional()
});
var BulkImportStudentsRequestSchema = z.object({
  tenantId: firestoreId,
  students: z.array(
    z.object({
      firstName: z.string().min(1).max(MAX_SHORT_TEXT),
      lastName: z.string().min(1).max(MAX_SHORT_TEXT),
      rollNumber: z.string().min(1).max(50),
      email: z.string().email().max(MAX_SHORT_TEXT).optional(),
      phone: z.string().max(20).optional(),
      classId: firestoreId.optional(),
      className: z.string().max(MAX_SHORT_TEXT).optional(),
      section: z.string().max(20).optional(),
      parentFirstName: z.string().max(MAX_SHORT_TEXT).optional(),
      parentLastName: z.string().max(MAX_SHORT_TEXT).optional(),
      parentEmail: z.string().email().max(MAX_SHORT_TEXT).optional(),
      parentPhone: z.string().max(20).optional()
    })
  ).max(MAX_BULK_ITEMS, "Maximum 500 students per import"),
  dryRun: z.boolean()
});
var CreateOrgUserRequestSchema = z.object({
  tenantId: firestoreId,
  role: z.enum(["teacher", "student", "parent", "scanner", "staff"]),
  email: z.string().email("Invalid email format").max(MAX_SHORT_TEXT).optional(),
  rollNumber: z.string().max(50).optional(),
  firstName: z.string().min(1, "First name is required").max(MAX_SHORT_TEXT),
  lastName: z.string().min(1, "Last name is required").max(MAX_SHORT_TEXT),
  password: z.string().min(6, "Password must be at least 6 characters").max(128).optional(),
  phone: z.string().max(20).optional(),
  classIds: z.array(firestoreId).max(MAX_ARRAY_ITEMS).optional(),
  subjects: z.array(z.string().max(MAX_SHORT_TEXT)).max(MAX_ARRAY_ITEMS).optional(),
  linkedStudentIds: z.array(firestoreId).max(MAX_ARRAY_ITEMS).optional()
});
var SwitchActiveTenantRequestSchema = z.object({
  tenantId: firestoreId
});
var JoinTenantRequestSchema = z.object({
  tenantCode: z.string().min(1, "Tenant code is required").max(50)
});
var SaveGlobalPresetRequestSchema = z.object({
  id: z.string().optional(),
  data: z.object({
    name: z.string().optional(),
    description: z.string().optional(),
    isDefault: z.boolean().optional(),
    isPublic: z.boolean().optional(),
    enabledDimensions: z.array(EvaluationDimensionSchema).optional(),
    displaySettings: z.object({
      showStrengths: z.boolean(),
      showKeyTakeaway: z.boolean(),
      prioritizeByImportance: z.boolean()
    }).optional()
  }).optional(),
  delete: z.boolean().optional()
});
var SaveSpaceRequestSchema = z.object({
  id: firestoreId.optional(),
  tenantId: firestoreId,
  data: z.object({
    title: z.string().max(MAX_SHORT_TEXT).optional(),
    description: z.string().max(MAX_MEDIUM_TEXT).optional(),
    thumbnailUrl: z.string().optional(),
    slug: z.string().optional(),
    type: z.enum(["learning", "practice", "assessment", "resource", "hybrid"]).optional(),
    subject: z.string().optional(),
    labels: z.array(z.string().max(MAX_SHORT_TEXT)).max(MAX_ARRAY_ITEMS).optional(),
    classIds: z.array(firestoreId).max(MAX_ARRAY_ITEMS).optional(),
    sectionIds: z.array(firestoreId).max(MAX_ARRAY_ITEMS).optional(),
    teacherIds: z.array(firestoreId).max(MAX_ARRAY_ITEMS).optional(),
    accessType: z.enum(["class_assigned", "tenant_wide", "public_store"]).optional(),
    academicSessionId: z.string().optional(),
    defaultEvaluatorAgentId: z.string().optional(),
    defaultTutorAgentId: z.string().optional(),
    defaultTimeLimitMinutes: z.number().optional(),
    allowRetakes: z.boolean().optional(),
    maxRetakes: z.number().optional(),
    showCorrectAnswers: z.boolean().optional(),
    defaultRubric: UnifiedRubricSchema.optional(),
    status: z.enum(["draft", "published", "archived"]).optional(),
    price: z.number().optional(),
    currency: z.string().optional(),
    publishedToStore: z.boolean().optional(),
    storeDescription: z.string().max(MAX_MEDIUM_TEXT).optional(),
    storeThumbnailUrl: z.string().optional()
  })
});
var SaveStoryPointRequestSchema = z.object({
  id: firestoreId.optional(),
  tenantId: firestoreId,
  spaceId: firestoreId,
  data: z.object({
    title: z.string().max(MAX_SHORT_TEXT).optional(),
    description: z.string().max(MAX_MEDIUM_TEXT).optional(),
    orderIndex: z.number().optional(),
    type: z.enum(["standard", "timed_test", "quiz", "practice", "test"]).optional(),
    sections: z.array(
      z.object({
        id: z.string(),
        title: z.string(),
        orderIndex: z.number(),
        description: z.string().optional()
      })
    ).optional(),
    assessmentConfig: z.object({
      durationMinutes: z.number().optional(),
      instructions: z.string().optional(),
      maxAttempts: z.number().optional(),
      shuffleQuestions: z.boolean().optional(),
      shuffleOptions: z.boolean().optional(),
      showResultsImmediately: z.boolean().optional()
    }).passthrough().optional(),
    defaultRubric: UnifiedRubricSchema.optional(),
    difficulty: z.enum(["easy", "medium", "hard", "expert"]).optional(),
    estimatedTimeMinutes: z.number().optional(),
    deleted: z.boolean().optional()
  })
});
var SaveItemRequestSchema = z.object({
  id: firestoreId.optional(),
  tenantId: firestoreId,
  spaceId: firestoreId,
  storyPointId: firestoreId,
  data: z.object({
    sectionId: firestoreId.optional(),
    type: z.enum([
      "question",
      "material",
      "interactive",
      "assessment",
      "discussion",
      "project",
      "checkpoint"
    ]).optional(),
    payload: z.record(z.string(), z.unknown()).optional(),
    title: z.string().max(MAX_SHORT_TEXT).optional(),
    content: z.string().max(MAX_LONG_TEXT).optional(),
    difficulty: z.enum(["easy", "medium", "hard"]).optional(),
    topics: z.array(z.string().max(MAX_SHORT_TEXT)).max(MAX_ARRAY_ITEMS).optional(),
    labels: z.array(z.string().max(MAX_SHORT_TEXT)).max(MAX_ARRAY_ITEMS).optional(),
    orderIndex: z.number().optional(),
    meta: ItemMetadataSchema.optional(),
    analytics: z.record(z.string(), z.number()).optional(),
    rubric: UnifiedRubricSchema.optional(),
    linkedQuestionId: z.string().optional(),
    attachments: z.array(
      z.object({
        id: z.string(),
        fileName: z.string().max(MAX_SHORT_TEXT),
        url: z.string(),
        type: z.enum(["image", "pdf", "audio"]),
        size: z.number(),
        mimeType: z.string()
      })
    ).max(20).optional(),
    deleted: z.boolean().optional()
  })
});
var StartTestSessionRequestSchema = z.object({
  tenantId: firestoreId,
  spaceId: firestoreId,
  storyPointId: firestoreId
});
var SubmitTestSessionRequestSchema = z.object({
  tenantId: firestoreId,
  sessionId: firestoreId,
  autoSubmitted: z.boolean().optional()
});
var EvaluateAnswerRequestSchema = z.object({
  tenantId: firestoreId,
  spaceId: firestoreId,
  storyPointId: firestoreId.optional(),
  itemId: firestoreId,
  answer: z.unknown(),
  mediaUrls: z.array(z.string().url()).max(20).optional()
});
var SendChatMessageRequestSchema = z.object({
  tenantId: firestoreId,
  spaceId: firestoreId,
  storyPointId: firestoreId,
  itemId: firestoreId,
  sessionId: firestoreId.optional(),
  message: z.string().min(1, "Message cannot be empty").max(MAX_LONG_TEXT),
  language: z.string().max(10).optional(),
  agentId: firestoreId.optional()
});
var StoredEvaluationSchema = z.object({
  score: z.number(),
  maxScore: z.number(),
  correctness: z.number(),
  percentage: z.number(),
  strengths: z.array(z.string()).max(20),
  weaknesses: z.array(z.string()).max(20),
  missingConcepts: z.array(z.string()).max(20),
  summary: z.object({
    keyTakeaway: z.string().max(MAX_MEDIUM_TEXT),
    overallComment: z.string().max(MAX_MEDIUM_TEXT)
  }).nullish(),
  mistakeClassification: z.enum(["Conceptual", "Silly Error", "Knowledge Gap", "None"]).nullish()
}).nullish();
var RecordItemAttemptRequestSchema = z.object({
  tenantId: firestoreId,
  spaceId: firestoreId,
  storyPointId: firestoreId,
  itemId: firestoreId,
  itemType: z.string(),
  score: z.number(),
  maxScore: z.number(),
  correct: z.boolean(),
  timeSpent: z.number().nullish(),
  feedback: z.string().max(MAX_MEDIUM_TEXT).nullish(),
  /** Student's answer for persistence (displayed on revisit). */
  answer: z.unknown().nullish(),
  /** Compact evaluation result for persistence (displayed on revisit). */
  evaluationData: StoredEvaluationSchema
});
var ListStoreSpacesRequestSchema = z.object({
  subject: z.string().max(MAX_SHORT_TEXT).optional(),
  limit: z.number().int().min(1).max(100).optional(),
  startAfter: firestoreId.optional(),
  search: z.string().max(MAX_SHORT_TEXT).optional()
});
var PurchaseSpaceRequestSchema = z.object({
  spaceId: firestoreId,
  paymentToken: z.string().max(MAX_SHORT_TEXT).optional()
});
var SaveExamRequestSchema = z.object({
  id: firestoreId.optional(),
  tenantId: firestoreId,
  data: z.object({
    title: z.string().max(MAX_SHORT_TEXT).optional(),
    subject: z.string().max(MAX_SHORT_TEXT).optional(),
    topics: z.array(z.string().max(MAX_SHORT_TEXT)).max(MAX_ARRAY_ITEMS).optional(),
    classIds: z.array(firestoreId).max(MAX_ARRAY_ITEMS).optional(),
    sectionIds: z.array(firestoreId).max(MAX_ARRAY_ITEMS).optional(),
    examDate: z.string().optional(),
    duration: z.number().optional(),
    academicSessionId: z.string().optional(),
    totalMarks: z.number().optional(),
    passingMarks: z.number().optional(),
    gradingConfig: z.object({
      autoGrade: z.boolean().optional(),
      allowRubricEdit: z.boolean().optional(),
      evaluationSettingsId: z.string().optional(),
      allowManualOverride: z.boolean().optional(),
      requireOverrideReason: z.boolean().optional(),
      releaseResultsAutomatically: z.boolean().optional()
    }).passthrough().optional(),
    linkedSpaceId: z.string().nullish(),
    linkedSpaceTitle: z.string().nullish(),
    linkedStoryPointId: z.string().nullish(),
    status: z.enum([
      "draft",
      "question_paper_uploaded",
      "question_paper_extracted",
      "published",
      "grading",
      "completed",
      "results_released",
      "archived"
    ]).optional(),
    evaluationSettingsId: z.string().optional(),
    // Storage paths under tenants/{tenantId}/... — read server-side via
    // bucket.file(path), not HTTPS URLs. Historical name kept.
    questionPaperImages: z.array(z.string().min(1).max(500)).max(50).nullish()
  })
});
var GradeQuestionRequestSchema = z.object({
  tenantId: firestoreId,
  mode: z.enum(["manual", "retry", "ai"]),
  submissionId: firestoreId.optional(),
  questionId: firestoreId.optional(),
  score: z.number().optional(),
  maxScore: z.number().optional(),
  feedback: z.string().max(MAX_MEDIUM_TEXT).optional(),
  examId: firestoreId.optional(),
  questionIds: z.array(firestoreId).max(MAX_ARRAY_ITEMS).optional()
});
var ExtractQuestionsRequestSchema = z.object({
  tenantId: firestoreId,
  examId: firestoreId,
  /** Set to 'single' to re-extract a single question */
  mode: z.enum(["full", "single"]).optional(),
  /** Required when mode='single' — the question number to re-extract */
  questionNumber: z.string().max(20).optional()
});
var UploadAnswerSheetsRequestSchema = z.object({
  tenantId: firestoreId,
  examId: firestoreId,
  studentId: firestoreId,
  classId: firestoreId,
  // Storage paths under tenants/{tenantId}/... — the backend reads them via
  // bucket.file(path), not as HTTPS URLs. The "Urls" name is historical.
  imageUrls: z.array(z.string().min(1).max(500)).min(1, "At least one image is required").max(50)
});
var SaveQuestionBankItemRequestSchema = z.object({
  id: firestoreId.optional(),
  tenantId: firestoreId,
  data: z.object({
    questionType: z.string().max(50).optional(),
    title: z.string().max(MAX_SHORT_TEXT).optional(),
    content: z.string().max(MAX_LONG_TEXT).optional(),
    explanation: z.string().max(MAX_MEDIUM_TEXT).optional(),
    basePoints: z.number().optional(),
    questionData: z.record(z.string(), z.unknown()).optional(),
    subject: z.string().max(MAX_SHORT_TEXT).optional(),
    topics: z.array(z.string().max(MAX_SHORT_TEXT)).max(MAX_ARRAY_ITEMS).optional(),
    difficulty: z.enum(["easy", "medium", "hard"]).optional(),
    bloomsLevel: z.enum(["remember", "understand", "apply", "analyze", "evaluate", "create"]).optional(),
    tags: z.array(z.string().max(MAX_SHORT_TEXT)).max(MAX_ARRAY_ITEMS).optional(),
    deleted: z.boolean().optional()
  })
});
var ListQuestionBankRequestSchema = z.object({
  tenantId: firestoreId,
  subject: z.string().max(MAX_SHORT_TEXT).optional(),
  topics: z.array(z.string().max(MAX_SHORT_TEXT)).max(MAX_ARRAY_ITEMS).optional(),
  difficulty: z.enum(["easy", "medium", "hard"]).optional(),
  bloomsLevel: z.enum(["remember", "understand", "apply", "analyze", "evaluate", "create"]).optional(),
  questionType: z.string().max(50).optional(),
  tags: z.array(z.string().max(MAX_SHORT_TEXT)).max(MAX_ARRAY_ITEMS).optional(),
  search: z.string().max(MAX_SHORT_TEXT).optional(),
  sortBy: z.enum(["usageCount", "averageScore", "createdAt"]).optional(),
  sortDir: z.enum(["asc", "desc"]).optional(),
  limit: z.number().optional(),
  startAfter: z.string().optional()
});
var ImportFromBankRequestSchema = z.object({
  tenantId: firestoreId,
  spaceId: firestoreId,
  storyPointId: firestoreId,
  sectionId: firestoreId.optional(),
  questionBankItemIds: z.array(firestoreId).min(1, "Select at least one item").max(MAX_ARRAY_ITEMS)
});
var SaveSpaceReviewRequestSchema = z.object({
  tenantId: firestoreId,
  spaceId: firestoreId,
  rating: z.number().int().min(1, "Rating must be at least 1").max(5, "Rating must be at most 5"),
  comment: z.string().max(MAX_MEDIUM_TEXT).optional()
});
var SaveRubricPresetRequestSchema = z.object({
  id: firestoreId.optional(),
  tenantId: firestoreId,
  data: z.object({
    name: z.string().max(MAX_SHORT_TEXT).optional(),
    description: z.string().max(MAX_MEDIUM_TEXT).optional(),
    rubric: UnifiedRubricSchema.optional(),
    category: z.enum(["general", "math", "science", "language", "coding", "essay", "custom"]).optional(),
    questionTypes: z.array(z.string().max(50)).max(MAX_ARRAY_ITEMS).optional(),
    deleted: z.boolean().optional()
  })
});
var GetSummaryRequestSchema = z.object({
  tenantId: firestoreId.optional(),
  scope: z.enum(["student", "class", "platform", "health"]),
  studentId: firestoreId.optional(),
  classId: firestoreId.optional()
});
var GenerateReportRequestSchema = z.object({
  tenantId: firestoreId,
  type: z.enum(["exam-result", "progress", "class"]),
  examId: firestoreId.optional(),
  studentId: firestoreId.optional(),
  classId: firestoreId.optional(),
  academicSessionId: firestoreId.optional()
});
var SaveAnnouncementRequestSchema = z.object({
  id: z.string().optional(),
  tenantId: z.string().optional(),
  data: z.object({
    title: z.string().max(200).optional(),
    body: z.string().max(5e3).optional(),
    scope: z.enum(["platform", "tenant"]).optional(),
    targetRoles: z.array(z.string()).max(10).optional(),
    targetClassIds: z.array(z.string()).max(100).optional(),
    status: z.enum(["draft", "published", "archived"]).optional(),
    expiresAt: z.string().optional()
  }),
  delete: z.boolean().optional()
});
var ListAnnouncementsRequestSchema = z.object({
  tenantId: z.string().optional(),
  scope: z.enum(["platform", "tenant"]).optional(),
  status: z.enum(["draft", "published", "archived"]).optional(),
  limit: z.number().min(1).max(100).optional(),
  cursor: z.string().optional()
});

// src/schemas/index.ts
var FirestoreTimestampSchema = z.object({
  seconds: z.number(),
  nanoseconds: z.number()
}).passthrough();
var TenantSchema = z.object({
  id: z.string(),
  name: z.string(),
  shortName: z.string().optional(),
  slug: z.string(),
  description: z.string().optional(),
  tenantCode: z.string(),
  ownerUid: z.string(),
  contactEmail: z.string(),
  contactPhone: z.string().optional(),
  contactPerson: z.string().optional(),
  logoUrl: z.string().optional(),
  bannerUrl: z.string().optional(),
  website: z.string().optional(),
  address: z.object({
    street: z.string().optional(),
    city: z.string().optional(),
    state: z.string().optional(),
    country: z.string().optional(),
    zipCode: z.string().optional()
  }).optional(),
  status: z.enum(["active", "suspended", "trial", "expired", "deactivated"]),
  subscription: z.object({
    plan: z.enum(["free", "trial", "basic", "premium", "enterprise"]),
    expiresAt: FirestoreTimestampSchema.optional(),
    maxStudents: z.number().optional(),
    maxTeachers: z.number().optional(),
    maxSpaces: z.number().optional(),
    maxExamsPerMonth: z.number().optional(),
    billingCycle: z.enum(["monthly", "annual"]).optional(),
    billingEmail: z.string().optional(),
    currentPeriodStart: FirestoreTimestampSchema.optional(),
    currentPeriodEnd: FirestoreTimestampSchema.optional(),
    cancelAtPeriodEnd: z.boolean().optional()
  }),
  features: z.object({
    autoGradeEnabled: z.boolean(),
    levelUpEnabled: z.boolean(),
    scannerAppEnabled: z.boolean(),
    aiChatEnabled: z.boolean(),
    aiGradingEnabled: z.boolean(),
    analyticsEnabled: z.boolean(),
    parentPortalEnabled: z.boolean(),
    bulkImportEnabled: z.boolean(),
    apiAccessEnabled: z.boolean()
  }),
  settings: z.object({
    geminiKeyRef: z.string().optional(),
    geminiKeySet: z.boolean(),
    defaultEvaluationSettingsId: z.string().optional(),
    defaultAiModel: z.string().optional(),
    timezone: z.string().optional(),
    locale: z.string().optional(),
    gradingPolicy: z.string().optional()
  }),
  stats: z.object({
    totalStudents: z.number(),
    totalTeachers: z.number(),
    totalClasses: z.number(),
    totalSpaces: z.number(),
    totalExams: z.number(),
    activeStudentsLast30Days: z.number().optional()
  }),
  branding: z.object({
    logoUrl: z.string().optional(),
    bannerUrl: z.string().optional(),
    primaryColor: z.string().optional(),
    accentColor: z.string().optional(),
    favicon: z.string().optional()
  }).optional(),
  usage: z.object({
    currentStudents: z.number(),
    currentTeachers: z.number(),
    currentSpaces: z.number(),
    examsThisMonth: z.number(),
    aiCallsThisMonth: z.number(),
    storageBytes: z.number(),
    lastUpdated: FirestoreTimestampSchema
  }).optional(),
  onboarding: z.object({
    completed: z.boolean(),
    completedSteps: z.array(z.string()),
    completedAt: FirestoreTimestampSchema.optional()
  }).optional(),
  createdAt: FirestoreTimestampSchema,
  updatedAt: FirestoreTimestampSchema
});
var ClassSchema = z.object({
  id: z.string(),
  tenantId: z.string(),
  name: z.string(),
  grade: z.string(),
  section: z.string().optional(),
  academicSessionId: z.string().optional(),
  teacherIds: z.array(z.string()),
  studentIds: z.array(z.string()),
  studentCount: z.number(),
  status: z.enum(["active", "archived"]),
  createdAt: FirestoreTimestampSchema,
  updatedAt: FirestoreTimestampSchema
});
var StudentSchema = z.object({
  id: z.string(),
  tenantId: z.string(),
  uid: z.string(),
  rollNumber: z.string().optional(),
  section: z.string().optional(),
  classIds: z.array(z.string()),
  parentIds: z.array(z.string()),
  grade: z.string().optional(),
  admissionNumber: z.string().optional(),
  dateOfBirth: z.string().optional(),
  status: z.enum(["active", "archived"]),
  createdAt: FirestoreTimestampSchema,
  updatedAt: FirestoreTimestampSchema
});
var TeacherSchema = z.object({
  id: z.string(),
  tenantId: z.string(),
  authUid: z.string().optional(),
  uid: z.string().optional(),
  email: z.string().optional(),
  phone: z.string().optional(),
  firstName: z.string(),
  lastName: z.string(),
  displayName: z.string().optional(),
  employeeId: z.string().optional(),
  department: z.string().optional(),
  subjects: z.array(z.string()),
  designation: z.string().optional(),
  classIds: z.array(z.string()),
  sectionIds: z.array(z.string()).optional(),
  status: z.enum(["active", "archived"]),
  lastLogin: FirestoreTimestampSchema.optional(),
  createdAt: FirestoreTimestampSchema,
  updatedAt: FirestoreTimestampSchema
});
var SpaceSchema = z.object({
  id: z.string(),
  tenantId: z.string(),
  title: z.string(),
  description: z.string().nullish(),
  thumbnailUrl: z.string().nullish(),
  slug: z.string().nullish(),
  type: z.enum(["learning", "practice", "assessment", "resource", "hybrid"]),
  subject: z.string().nullish(),
  labels: z.array(z.string()).nullish(),
  classIds: z.array(z.string()),
  sectionIds: z.array(z.string()).nullish(),
  teacherIds: z.array(z.string()),
  accessType: z.enum(["class_assigned", "tenant_wide", "public_store"]),
  academicSessionId: z.string().nullish(),
  status: z.enum(["draft", "published", "archived"]),
  createdBy: z.string(),
  createdAt: FirestoreTimestampSchema,
  updatedAt: FirestoreTimestampSchema
}).passthrough();
var ExamSchema = z.object({
  id: z.string(),
  tenantId: z.string(),
  title: z.string(),
  subject: z.string(),
  topics: z.array(z.string()).optional().default([]),
  classIds: z.array(z.string()).optional().default([]),
  sectionIds: z.array(z.string()).optional(),
  // examDate may be null in Firestore when an exam was created without a date.
  examDate: FirestoreTimestampSchema.nullish(),
  duration: z.number().optional().default(0),
  academicSessionId: z.string().nullish(),
  totalMarks: z.number().optional().default(0),
  passingMarks: z.number().optional().default(0),
  status: z.enum([
    "draft",
    "question_paper_uploaded",
    "question_paper_extracted",
    "published",
    "grading",
    "completed",
    "results_released",
    "archived"
  ]),
  createdBy: z.string().optional(),
  createdAt: FirestoreTimestampSchema,
  updatedAt: FirestoreTimestampSchema
}).passthrough();
var SubmissionSchema = z.object({
  id: z.string(),
  tenantId: z.string(),
  examId: z.string(),
  studentId: z.string(),
  studentName: z.string(),
  rollNumber: z.string(),
  classId: z.string(),
  answerSheets: z.object({
    images: z.array(z.string()),
    uploadedAt: FirestoreTimestampSchema,
    uploadedBy: z.string(),
    uploadSource: z.enum(["web", "scanner", "gcs"])
  }),
  summary: z.object({
    totalScore: z.number(),
    maxScore: z.number(),
    percentage: z.number(),
    grade: z.string(),
    questionsGraded: z.number(),
    totalQuestions: z.number(),
    completedAt: FirestoreTimestampSchema.optional()
  }),
  pipelineStatus: z.enum([
    "uploaded",
    "ocr_processing",
    "ocr_failed",
    "scouting",
    "scouting_failed",
    "scouting_complete",
    "grading",
    "grading_partial",
    "grading_failed",
    "grading_complete",
    "finalization_failed",
    "ready_for_review",
    "reviewed",
    "failed",
    "manual_review_needed"
  ]),
  pipelineError: z.string().optional(),
  retryCount: z.number(),
  resultsReleased: z.boolean(),
  resultsReleasedAt: FirestoreTimestampSchema.optional(),
  resultsReleasedBy: z.string().optional(),
  createdAt: FirestoreTimestampSchema,
  updatedAt: FirestoreTimestampSchema
});
var ExamQuestionSchema = z.object({
  id: z.string(),
  examId: z.string(),
  text: z.string(),
  imageUrls: z.array(z.string()).optional(),
  maxMarks: z.number(),
  order: z.number(),
  questionType: z.enum(["standard", "diagram", "multi-part"]).optional(),
  extractedBy: z.enum(["ai", "manual"]).optional(),
  extractedAt: FirestoreTimestampSchema.optional(),
  createdAt: FirestoreTimestampSchema,
  updatedAt: FirestoreTimestampSchema
}).passthrough();
var QuestionSubmissionSchema = z.object({
  id: z.string(),
  submissionId: z.string(),
  questionId: z.string(),
  examId: z.string(),
  mapping: z.object({
    pageIndices: z.array(z.number()),
    imageUrls: z.array(z.string()),
    scoutedAt: FirestoreTimestampSchema
  }),
  gradingStatus: z.enum(["pending", "processing", "graded", "needs_review", "failed", "manual", "overridden"]),
  gradingError: z.string().optional(),
  gradingRetryCount: z.number(),
  createdAt: FirestoreTimestampSchema,
  updatedAt: FirestoreTimestampSchema
}).passthrough();
var ParentSchema = z.object({
  id: z.string(),
  tenantId: z.string(),
  authUid: z.string().optional(),
  email: z.string().optional(),
  phone: z.string().optional(),
  firstName: z.string(),
  lastName: z.string(),
  displayName: z.string().optional(),
  studentIds: z.array(z.string()),
  status: z.enum(["active", "archived"]),
  lastLogin: FirestoreTimestampSchema.optional(),
  createdAt: FirestoreTimestampSchema,
  updatedAt: FirestoreTimestampSchema
});
var AcademicSessionSchema = z.object({
  id: z.string(),
  tenantId: z.string(),
  name: z.string(),
  startDate: FirestoreTimestampSchema,
  endDate: FirestoreTimestampSchema,
  isCurrent: z.boolean(),
  status: z.enum(["active", "archived"]),
  createdAt: FirestoreTimestampSchema,
  updatedAt: FirestoreTimestampSchema
});
var StoryPointSchema = z.object({
  id: z.string(),
  tenantId: z.string(),
  spaceId: z.string(),
  title: z.string(),
  description: z.string().nullish(),
  orderIndex: z.number(),
  type: z.enum(["standard", "timed_test", "quiz", "practice", "test"]),
  sections: z.array(
    z.object({
      id: z.string(),
      title: z.string(),
      orderIndex: z.number(),
      description: z.string().nullish()
    })
  ).nullish(),
  assessmentConfig: z.object({
    durationMinutes: z.number().nullish(),
    instructions: z.string().nullish(),
    maxAttempts: z.number().nullish(),
    shuffleQuestions: z.boolean().nullish(),
    shuffleOptions: z.boolean().nullish(),
    showResultsImmediately: z.boolean().nullish()
  }).passthrough().nullish(),
  difficulty: z.enum(["easy", "medium", "hard", "expert"]).nullish(),
  estimatedTimeMinutes: z.number().nullish(),
  status: z.enum(["active", "archived"]).nullish(),
  createdAt: FirestoreTimestampSchema,
  updatedAt: FirestoreTimestampSchema
}).passthrough();
var AgentSchema = z.object({
  id: z.string(),
  tenantId: z.string(),
  spaceId: z.string(),
  name: z.string(),
  type: z.enum(["tutor", "evaluator"]),
  systemPrompt: z.string(),
  rules: z.array(z.string()).optional(),
  modelOverride: z.string().optional(),
  isActive: z.boolean(),
  createdAt: FirestoreTimestampSchema,
  updatedAt: FirestoreTimestampSchema
}).passthrough();
var ChatMessageSchema = z.object({
  id: z.string(),
  role: z.enum(["user", "assistant", "system"]),
  text: z.string(),
  timestamp: FirestoreTimestampSchema,
  mediaUrls: z.array(z.string()).optional(),
  tokensUsed: z.number().optional()
});
var ChatSessionSchema = z.object({
  id: z.string(),
  tenantId: z.string(),
  userId: z.string(),
  spaceId: z.string(),
  storyPointId: z.string().optional(),
  itemId: z.string().optional(),
  agentId: z.string().optional(),
  messages: z.array(ChatMessageSchema),
  systemPrompt: z.string().optional(),
  isActive: z.boolean(),
  createdAt: FirestoreTimestampSchema,
  updatedAt: FirestoreTimestampSchema
});
var DigitalTestSessionSchema = z.object({
  id: z.string(),
  tenantId: z.string(),
  userId: z.string(),
  spaceId: z.string(),
  storyPointId: z.string(),
  type: z.enum(["timed_test", "quiz", "practice"]),
  status: z.enum(["in_progress", "completed", "expired", "abandoned"]),
  startedAt: FirestoreTimestampSchema,
  deadline: FirestoreTimestampSchema.nullish(),
  completedAt: FirestoreTimestampSchema.nullish(),
  totalQuestions: z.number(),
  attemptNumber: z.number(),
  createdAt: FirestoreTimestampSchema,
  updatedAt: FirestoreTimestampSchema
}).passthrough();
var NotificationSchema = z.object({
  id: z.string(),
  tenantId: z.string(),
  recipientUid: z.string(),
  recipientRole: z.enum(["teacher", "student", "parent", "tenantAdmin"]),
  type: z.string(),
  title: z.string(),
  body: z.string(),
  isRead: z.boolean(),
  entityType: z.string().optional(),
  entityId: z.string().optional(),
  createdAt: FirestoreTimestampSchema
}).passthrough();
var PurchaseRecordSchema = z.object({
  spaceId: z.string(),
  spaceTitle: z.string(),
  amount: z.number(),
  currency: z.string(),
  purchasedAt: FirestoreTimestampSchema,
  transactionId: z.string()
}).passthrough();
var ConsumerProfileSchema = z.object({
  plan: z.enum(["free", "pro", "premium"]),
  enrolledSpaceIds: z.array(z.string()),
  purchaseHistory: z.array(PurchaseRecordSchema),
  totalSpend: z.number()
}).passthrough();
var UnifiedUserSchema = z.object({
  uid: z.string(),
  email: z.string().nullish(),
  phone: z.string().nullish(),
  authProviders: z.array(z.enum(["email", "phone", "google", "apple"])),
  displayName: z.string(),
  firstName: z.string().nullish(),
  lastName: z.string().nullish(),
  photoURL: z.string().nullish(),
  country: z.string().optional(),
  age: z.number().optional(),
  grade: z.string().optional(),
  onboardingCompleted: z.boolean().optional(),
  preferences: z.record(z.string(), z.unknown()).optional(),
  isSuperAdmin: z.boolean(),
  consumerProfile: ConsumerProfileSchema.optional(),
  activeTenantId: z.string().optional(),
  createdAt: FirestoreTimestampSchema,
  updatedAt: FirestoreTimestampSchema,
  lastLogin: FirestoreTimestampSchema.optional(),
  status: z.enum(["active", "suspended", "deleted"])
}).passthrough();
var TeacherPermissionsSchema = z.object({
  canCreateExams: z.boolean().optional(),
  canEditRubrics: z.boolean().optional(),
  canManuallyGrade: z.boolean().optional(),
  canViewAllExams: z.boolean().optional(),
  canCreateSpaces: z.boolean().optional(),
  canManageContent: z.boolean().optional(),
  canViewAnalytics: z.boolean().optional(),
  canConfigureAgents: z.boolean().optional(),
  managedSpaceIds: z.array(z.string()).optional(),
  managedClassIds: z.array(z.string()).optional()
}).passthrough();
var StaffPermissionsSchema = z.object({
  canManageUsers: z.boolean(),
  canManageClasses: z.boolean(),
  canManageBilling: z.boolean(),
  canViewAnalytics: z.boolean(),
  canManageSettings: z.boolean(),
  canExportData: z.boolean()
}).passthrough();
var UserMembershipSchema = z.object({
  id: z.string(),
  uid: z.string(),
  tenantId: z.string(),
  tenantCode: z.string(),
  role: z.enum(["superAdmin", "tenantAdmin", "teacher", "student", "parent", "scanner", "staff"]),
  status: z.enum(["active", "inactive", "suspended"]),
  joinSource: z.enum([
    "admin_created",
    "bulk_import",
    "invite_code",
    "self_register",
    "migration",
    "tenant_code"
  ]),
  teacherId: z.string().optional(),
  studentId: z.string().optional(),
  parentId: z.string().optional(),
  scannerId: z.string().optional(),
  schoolId: z.string().optional(),
  staffId: z.string().optional(),
  permissions: TeacherPermissionsSchema.optional(),
  staffPermissions: StaffPermissionsSchema.optional(),
  parentLinkedStudentIds: z.array(z.string()).optional(),
  createdAt: FirestoreTimestampSchema,
  updatedAt: FirestoreTimestampSchema,
  lastActive: FirestoreTimestampSchema.optional()
}).passthrough();
var UnifiedItemSchema = z.object({
  id: z.string(),
  spaceId: z.string(),
  storyPointId: z.string(),
  sectionId: z.string().nullish(),
  tenantId: z.string(),
  type: z.enum([
    "question",
    "material",
    "interactive",
    "assessment",
    "discussion",
    "project",
    "checkpoint"
  ]),
  payload: z.record(z.string(), z.unknown()),
  title: z.string().nullish(),
  content: z.string().nullish(),
  difficulty: z.enum(["easy", "medium", "hard"]).nullish(),
  topics: z.array(z.string()).nullish(),
  labels: z.array(z.string()).nullish(),
  orderIndex: z.number(),
  meta: z.record(z.string(), z.unknown()).nullish(),
  analytics: z.record(z.string(), z.unknown()).nullish(),
  rubric: z.record(z.string(), z.unknown()).nullish(),
  linkedQuestionId: z.string().nullish(),
  version: z.number().nullish(),
  createdBy: z.string().nullish(),
  createdAt: FirestoreTimestampSchema,
  updatedAt: FirestoreTimestampSchema
}).passthrough();
var QuestionProgressDataSchema = z.object({
  status: z.enum(["pending", "correct", "incorrect", "partial"]),
  attemptsCount: z.number(),
  bestScore: z.number(),
  pointsEarned: z.number(),
  totalPoints: z.number(),
  percentage: z.number(),
  solved: z.boolean(),
  latestScore: z.number().optional(),
  latestStatus: z.enum(["pending", "correct", "incorrect", "partial"]).optional()
}).passthrough();
var StoredEvaluationZodSchema = z.object({
  score: z.number(),
  maxScore: z.number(),
  correctness: z.number(),
  percentage: z.number(),
  strengths: z.array(z.string()),
  weaknesses: z.array(z.string()),
  missingConcepts: z.array(z.string()),
  summary: z.object({
    keyTakeaway: z.string(),
    overallComment: z.string()
  }).optional(),
  mistakeClassification: z.enum(["Conceptual", "Silly Error", "Knowledge Gap", "None"]).optional()
}).passthrough();
var AttemptRecordSchema = z.object({
  attemptNumber: z.number(),
  answer: z.unknown(),
  evaluation: StoredEvaluationZodSchema,
  score: z.number(),
  maxScore: z.number(),
  timestamp: z.number()
}).passthrough();
var ItemProgressEntrySchema = z.object({
  itemId: z.string(),
  itemType: z.enum([
    "question",
    "material",
    "interactive",
    "assessment",
    "discussion",
    "project",
    "checkpoint"
  ]),
  completed: z.boolean(),
  completedAt: z.number().optional(),
  timeSpent: z.number().optional(),
  interactions: z.number().optional(),
  lastUpdatedAt: z.number(),
  questionData: QuestionProgressDataSchema.optional(),
  progress: z.number().optional(),
  score: z.number().optional(),
  feedback: z.string().optional(),
  lastAnswer: z.unknown().optional(),
  lastEvaluation: StoredEvaluationZodSchema.optional(),
  attempts: z.array(AttemptRecordSchema).max(20).optional()
}).passthrough();
var StoryPointProgressSchema = z.object({
  storyPointId: z.string(),
  status: z.enum(["not_started", "in_progress", "completed"]),
  pointsEarned: z.number(),
  totalPoints: z.number(),
  percentage: z.number(),
  completedItems: z.number(),
  totalItems: z.number(),
  completedAt: z.number().optional()
}).passthrough();
var StoryPointProgressDocSchema = z.object({
  storyPointId: z.string(),
  status: z.enum(["not_started", "in_progress", "completed"]),
  pointsEarned: z.number(),
  totalPoints: z.number(),
  percentage: z.number(),
  completedItems: z.number(),
  totalItems: z.number(),
  completedAt: z.number().optional(),
  updatedAt: z.number(),
  items: z.record(z.string(), ItemProgressEntrySchema)
}).passthrough();
var SpaceProgressSchema = z.object({
  id: z.string(),
  userId: z.string(),
  tenantId: z.string(),
  spaceId: z.string(),
  status: z.enum(["not_started", "in_progress", "completed"]),
  pointsEarned: z.number(),
  totalPoints: z.number(),
  marksEarned: z.number().optional(),
  totalMarks: z.number().optional(),
  percentage: z.number(),
  storyPoints: z.record(z.string(), StoryPointProgressSchema),
  startedAt: FirestoreTimestampSchema.optional(),
  completedAt: FirestoreTimestampSchema.optional(),
  updatedAt: FirestoreTimestampSchema
}).passthrough();
var QuestionBankItemSchema = z.object({
  id: z.string(),
  tenantId: z.string(),
  questionType: z.enum([
    "mcq",
    "mcaq",
    "true-false",
    "numerical",
    "text",
    "paragraph",
    "code",
    "fill-blanks",
    "fill-blanks-dd",
    "matching",
    "jumbled",
    "audio",
    "image_evaluation",
    "group-options",
    "chat_agent_question"
  ]),
  title: z.string().optional(),
  content: z.string(),
  explanation: z.string().optional(),
  basePoints: z.number().optional(),
  questionData: z.record(z.string(), z.unknown()),
  subject: z.string(),
  topics: z.array(z.string()),
  difficulty: z.enum(["easy", "medium", "hard"]),
  bloomsLevel: z.enum(["remember", "understand", "apply", "analyze", "evaluate", "create"]).optional(),
  usageCount: z.number(),
  averageScore: z.number().optional(),
  lastUsedAt: FirestoreTimestampSchema.optional(),
  tags: z.array(z.string()),
  createdBy: z.string(),
  createdAt: FirestoreTimestampSchema,
  updatedAt: FirestoreTimestampSchema
}).passthrough();
var SpaceReviewSchema = z.object({
  id: z.string(),
  spaceId: z.string(),
  tenantId: z.string(),
  userId: z.string(),
  userName: z.string().optional(),
  rating: z.number(),
  comment: z.string().optional(),
  createdAt: FirestoreTimestampSchema,
  updatedAt: FirestoreTimestampSchema
}).passthrough();
var AnswerKeySchema = z.object({
  id: z.string(),
  itemId: z.string(),
  questionType: z.enum([
    "mcq",
    "mcaq",
    "true-false",
    "numerical",
    "text",
    "paragraph",
    "code",
    "fill-blanks",
    "fill-blanks-dd",
    "matching",
    "jumbled",
    "audio",
    "image_evaluation",
    "group-options",
    "chat_agent_question"
  ]),
  correctAnswer: z.unknown(),
  acceptableAnswers: z.array(z.unknown()).optional(),
  evaluationGuidance: z.string().optional(),
  modelAnswer: z.string().optional(),
  createdAt: FirestoreTimestampSchema,
  updatedAt: FirestoreTimestampSchema
}).passthrough();
var EvaluationDimensionSchema2 = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string(),
  icon: z.string().optional(),
  priority: z.enum(["HIGH", "MEDIUM", "LOW"]),
  promptGuidance: z.string(),
  enabled: z.boolean(),
  isDefault: z.boolean(),
  isCustom: z.boolean(),
  expectedFeedbackCount: z.number().optional(),
  weight: z.number(),
  scoringScale: z.number(),
  createdAt: FirestoreTimestampSchema.optional(),
  createdBy: z.string().optional()
}).passthrough();
var EvaluationDisplaySettingsSchema = z.object({
  showStrengths: z.boolean(),
  showKeyTakeaway: z.boolean(),
  prioritizeByImportance: z.boolean()
}).passthrough();
var EvaluationConfidenceConfigSchema = z.object({
  confidenceThreshold: z.number(),
  autoApproveThreshold: z.number(),
  requireReviewForPartialCredit: z.boolean()
}).passthrough();
var UsageQuotaConfigSchema = z.object({
  monthlyBudgetUsd: z.number(),
  dailyCallLimit: z.number(),
  warningThresholdPercent: z.number()
}).passthrough();
var EvaluationSettingsSchema = z.object({
  id: z.string(),
  tenantId: z.string(),
  name: z.string(),
  description: z.string().optional(),
  isDefault: z.boolean(),
  isPublic: z.boolean().optional(),
  enabledDimensions: z.array(EvaluationDimensionSchema2),
  displaySettings: EvaluationDisplaySettingsSchema,
  confidenceConfig: EvaluationConfidenceConfigSchema.optional(),
  usageQuota: UsageQuotaConfigSchema.optional(),
  createdBy: z.string().optional(),
  createdAt: FirestoreTimestampSchema,
  updatedAt: FirestoreTimestampSchema
}).passthrough();
var GradingDeadLetterSchema = z.object({
  id: z.string(),
  submissionId: z.string(),
  questionSubmissionId: z.string().optional(),
  pipelineStep: z.enum(["ocr", "scouting", "grading"]),
  error: z.string(),
  errorStack: z.string().optional(),
  attempts: z.number(),
  lastAttemptAt: FirestoreTimestampSchema,
  resolvedAt: FirestoreTimestampSchema.optional(),
  resolvedBy: z.string().optional(),
  resolutionMethod: z.enum(["retry_success", "manual_grade", "dismissed"]).optional(),
  createdAt: FirestoreTimestampSchema
}).passthrough();
var ScoreDistributionBucketSchema = z.object({
  min: z.number(),
  max: z.number(),
  count: z.number()
}).passthrough();
var QuestionAnalyticsEntrySchema = z.object({
  questionId: z.string(),
  avgScore: z.number(),
  maxScore: z.number(),
  avgPercentage: z.number(),
  difficultyIndex: z.number(),
  discriminationIndex: z.number(),
  commonMistakes: z.array(z.string()),
  commonStrengths: z.array(z.string())
}).passthrough();
var ClassBreakdownEntrySchema = z.object({
  classId: z.string(),
  className: z.string(),
  avgScore: z.number(),
  passRate: z.number(),
  submissionCount: z.number()
}).passthrough();
var TopicPerformanceEntrySchema = z.object({
  topic: z.string(),
  avgPercentage: z.number(),
  weakStudentCount: z.number()
}).passthrough();
var ExamAnalyticsSchema = z.object({
  id: z.string(),
  tenantId: z.string(),
  examId: z.string(),
  totalSubmissions: z.number(),
  gradedSubmissions: z.number(),
  avgScore: z.number(),
  avgPercentage: z.number(),
  passRate: z.number(),
  medianScore: z.number(),
  scoreDistribution: z.object({
    buckets: z.array(ScoreDistributionBucketSchema),
    gradeDistribution: z.record(z.string(), z.number()).optional()
  }).passthrough(),
  questionAnalytics: z.record(z.string(), QuestionAnalyticsEntrySchema),
  classBreakdown: z.record(z.string(), ClassBreakdownEntrySchema),
  topicPerformance: z.record(z.string(), TopicPerformanceEntrySchema),
  computedAt: FirestoreTimestampSchema,
  lastUpdatedAt: FirestoreTimestampSchema
}).passthrough();
var AutogradeSubjectBreakdownSchema = z.object({
  avgScore: z.number(),
  examCount: z.number()
}).passthrough();
var RecentExamEntrySchema = z.object({
  examId: z.string(),
  examTitle: z.string(),
  score: z.number(),
  percentage: z.number(),
  date: FirestoreTimestampSchema
}).passthrough();
var StudentAutogradeMetricsSchema = z.object({
  totalExams: z.number(),
  completedExams: z.number(),
  averageScore: z.number(),
  averagePercentage: z.number(),
  totalMarksObtained: z.number(),
  totalMarksAvailable: z.number(),
  subjectBreakdown: z.record(z.string(), AutogradeSubjectBreakdownSchema),
  recentExams: z.array(RecentExamEntrySchema)
}).passthrough();
var LevelupSubjectBreakdownSchema = z.object({
  avgCompletion: z.number(),
  spaceCount: z.number()
}).passthrough();
var RecentActivityEntrySchema = z.object({
  spaceId: z.string(),
  spaceTitle: z.string(),
  action: z.string(),
  date: FirestoreTimestampSchema
}).passthrough();
var StudentLevelupMetricsSchema = z.object({
  totalSpaces: z.number(),
  completedSpaces: z.number(),
  averageCompletion: z.number(),
  totalPointsEarned: z.number(),
  totalPointsAvailable: z.number(),
  averageAccuracy: z.number(),
  streakDays: z.number(),
  subjectBreakdown: z.record(z.string(), LevelupSubjectBreakdownSchema),
  recentActivity: z.array(RecentActivityEntrySchema)
}).passthrough();
var StudentProgressSummarySchema = z.object({
  id: z.string(),
  tenantId: z.string(),
  studentId: z.string(),
  autograde: StudentAutogradeMetricsSchema,
  levelup: StudentLevelupMetricsSchema,
  overallScore: z.number(),
  strengthAreas: z.array(z.string()),
  weaknessAreas: z.array(z.string()),
  isAtRisk: z.boolean(),
  atRiskReasons: z.array(z.string()),
  lastUpdatedAt: FirestoreTimestampSchema
}).passthrough();
var ClassAutogradeMetricsSchema = z.object({
  averageClassScore: z.number(),
  examCompletionRate: z.number(),
  topPerformers: z.array(
    z.object({
      studentId: z.string(),
      name: z.string(),
      avgScore: z.number()
    })
  ),
  bottomPerformers: z.array(
    z.object({
      studentId: z.string(),
      name: z.string(),
      avgScore: z.number()
    })
  )
}).passthrough();
var ClassLevelupMetricsSchema = z.object({
  averageClassCompletion: z.number(),
  activeStudentRate: z.number(),
  topPointEarners: z.array(
    z.object({
      studentId: z.string(),
      name: z.string(),
      points: z.number()
    })
  )
}).passthrough();
var ClassProgressSummarySchema = z.object({
  id: z.string(),
  tenantId: z.string(),
  classId: z.string(),
  className: z.string(),
  studentCount: z.number(),
  autograde: ClassAutogradeMetricsSchema,
  levelup: ClassLevelupMetricsSchema,
  atRiskStudentIds: z.array(z.string()),
  atRiskCount: z.number(),
  lastUpdatedAt: FirestoreTimestampSchema
}).passthrough();
var LearningInsightSchema = z.object({
  id: z.string(),
  tenantId: z.string(),
  studentId: z.string(),
  type: z.enum([
    "weak_topic_recommendation",
    "exam_preparation",
    "streak_encouragement",
    "improvement_celebration",
    "at_risk_intervention",
    "cross_system_correlation"
  ]),
  priority: z.enum(["high", "medium", "low"]),
  title: z.string(),
  description: z.string(),
  actionType: z.enum(["practice_space", "review_exam", "seek_help", "celebrate"]),
  actionEntityId: z.string().optional(),
  actionEntityTitle: z.string().optional(),
  createdAt: FirestoreTimestampSchema,
  dismissedAt: FirestoreTimestampSchema.optional()
}).passthrough();
var LLMCallLogSchema = z.object({
  id: z.string(),
  tenantId: z.string(),
  functionName: z.string(),
  model: z.string(),
  inputTokens: z.number(),
  outputTokens: z.number(),
  totalTokens: z.number(),
  costUSD: z.number(),
  latencyMs: z.number(),
  status: z.enum(["success", "error"]),
  errorMessage: z.string().optional(),
  userId: z.string().optional(),
  examId: z.string().optional(),
  spaceId: z.string().optional(),
  createdAt: FirestoreTimestampSchema
}).passthrough();
var AchievementCriteriaSchema = z.object({
  type: z.enum([
    "spaces_completed",
    "story_points_completed",
    "exams_passed",
    "perfect_scores",
    "streak_days",
    "total_points",
    "items_completed",
    "chat_sessions",
    "leaderboard_top3",
    "login_days"
  ]),
  threshold: z.number(),
  subject: z.string().optional(),
  spaceId: z.string().optional()
}).passthrough();
var AchievementSchema = z.object({
  id: z.string(),
  tenantId: z.string(),
  title: z.string(),
  description: z.string(),
  icon: z.string(),
  category: z.enum([
    "learning",
    "consistency",
    "excellence",
    "exploration",
    "social",
    "milestone"
  ]),
  rarity: z.enum(["common", "uncommon", "rare", "epic", "legendary"]),
  tier: z.enum(["bronze", "silver", "gold", "platinum", "diamond"]),
  criteria: AchievementCriteriaSchema,
  pointsReward: z.number(),
  isActive: z.boolean(),
  createdAt: FirestoreTimestampSchema,
  updatedAt: FirestoreTimestampSchema
}).passthrough();
var StudentAchievementSchema = z.object({
  id: z.string(),
  tenantId: z.string(),
  userId: z.string(),
  achievementId: z.string(),
  achievement: AchievementSchema,
  earnedAt: FirestoreTimestampSchema,
  seen: z.boolean()
}).passthrough();
var StudentLevelSchema = z.object({
  id: z.string(),
  tenantId: z.string(),
  userId: z.string(),
  level: z.number(),
  currentXP: z.number(),
  xpToNextLevel: z.number(),
  totalXP: z.number(),
  tier: z.enum(["bronze", "silver", "gold", "platinum", "diamond"]),
  achievementCount: z.number(),
  updatedAt: FirestoreTimestampSchema
}).passthrough();

export { AI_EVALUATABLE_TYPES, APP_ERROR_TO_HTTPS, AUTO_EVALUATABLE_TYPES, AcademicSessionSchema, AchievementSchema, AgentSchema, AnswerKeySchema, BLOOMS_LEVELS, BulkImportStudentsRequestSchema, ChatMessageSchema, ChatSessionSchema, ClassProgressSummarySchema, ClassSchema, CreateOrgUserRequestSchema, DEFAULT_STAFF_PERMISSIONS, DEFAULT_TEACHER_PERMISSIONS, DeactivateTenantRequestSchema, DigitalTestSessionSchema, ERROR_MESSAGES, ERROR_RECOVERY_HINTS, EXAM_STATUSES, EvaluateAnswerRequestSchema, EvaluationSettingsSchema, ExamAnalyticsSchema, ExamQuestionSchema, ExamSchema, ExportTenantDataRequestSchema, ExtractQuestionsRequestSchema, GRADE_THRESHOLDS, GenerateReportRequestSchema, GetSummaryRequestSchema, GradeQuestionRequestSchema, GradingDeadLetterSchema, HTTPS_TO_APP_ERROR, ImportFromBankRequestSchema, JoinTenantRequestSchema, LLMCallLogSchema, LearningInsightSchema, ListAnnouncementsRequestSchema, ListQuestionBankRequestSchema, ListStoreSpacesRequestSchema, MAX_CLAIM_CLASS_IDS, ManageNotificationsRequestSchema, NotificationSchema, ParentSchema, PurchaseSpaceRequestSchema, QUESTION_GRADING_STATUSES, QuestionBankItemSchema, QuestionSubmissionSchema, RATE_LIMITS, ReactivateTenantRequestSchema, RecordItemAttemptRequestSchema, SUBMISSION_PIPELINE_STATUSES, SaveAcademicSessionRequestSchema, SaveAnnouncementRequestSchema, SaveClassRequestSchema, SaveExamRequestSchema, SaveGlobalPresetRequestSchema, SaveItemRequestSchema, SaveParentRequestSchema, SaveQuestionBankItemRequestSchema, SaveRubricPresetRequestSchema, SaveSpaceRequestSchema, SaveSpaceReviewRequestSchema, SaveStaffRequestSchema, SaveStoryPointRequestSchema, SaveStudentRequestSchema, SaveTeacherRequestSchema, SaveTenantRequestSchema, SendChatMessageRequestSchema, SpaceProgressSchema, SpaceReviewSchema, SpaceSchema, StartTestSessionRequestSchema, StoryPointProgressDocSchema, StoryPointSchema, StudentAchievementSchema, StudentLevelSchema, StudentProgressSummarySchema, StudentSchema, SubmissionSchema, SubmitTestSessionRequestSchema, SwitchActiveTenantRequestSchema, TeacherSchema, TenantSchema, UnifiedItemSchema, UnifiedUserSchema, UploadAnswerSheetsRequestSchema, UserMembershipSchema, asAcademicSessionId, asAgentId, asClassId, asExamId, asItemId, asParentId, asSessionId, asSpaceId, asStoryPointId, asStudentId, asSubmissionId, asTeacherId, asTenantId, asUserId, isAIEvaluatable, isAutoEvaluatable, isFirestoreTimestamp, isMaterialItem, isNonEmptyString, isQuestionItem, isValidDocumentId };
//# sourceMappingURL=index.mjs.map
//# sourceMappingURL=index.mjs.map